import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  GoogleAuthProvider,
  OAuthProvider,
  createUserWithEmailAndPassword,
  getRedirectResult,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithRedirect,
  signOut,
  updateProfile,
  type User,
} from "firebase/auth";
import type { MemberUser } from "@/lib/types";
import * as api from "@/lib/api";
import { getFirebaseAuth, isFirebaseConfigured } from "@/lib/firebase";

type AuthContextValue = {
  /** Vite の VITE_FIREBASE_* が最低限そろっているか */
  firebaseConfigured: boolean;
  firebaseUser: User | null;
  user: MemberUser | null;
  loading: boolean;
  authReady: boolean;
  error: string | null;
  clearError: () => void;
  getIdToken: (forceRefresh?: boolean) => Promise<string | null>;
  loginWithEmail: (email: string, password: string) => Promise<void>;
  registerWithEmail: (
    name: string,
    email: string,
    password: string,
    consents: api.RegistrationConsents,
    birthMonth: number,
  ) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithApple: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

/** Capacitor の WKWebView で getRedirectResult が長く待つことがあるため上限を付与 */
const GET_REDIRECT_RESULT_MS = 8_000;
/** onAuthStateChanged が一度も来ない場合のフォールバック（無限スプラッシュ防止） */
const AUTH_STATE_STALL_MS = 12_000;

async function loadMemberProfile(fbUser: User): Promise<MemberUser> {
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const { user } = await api.fetchMe(await fbUser.getIdToken(attempt > 0));
      return user;
    } catch (e) {
      const err = e instanceof Error ? e : new Error("unknown");
      if (!err.message.includes("会員情報が見つかりません")) throw err;
      await new Promise((r) => setTimeout(r, 150));
    }
  }

  const name =
    fbUser.displayName?.trim() ||
    (fbUser.email ? fbUser.email.split("@")[0] : "") ||
    "ゲスト";
  await api.profileInit(await fbUser.getIdToken(true), { name });
  const { user } = await api.fetchMe(await fbUser.getIdToken(true));
  return user;
}

function firebaseErrorCode(e: unknown): string {
  if (typeof e === "object" && e && "code" in e && typeof (e as { code: unknown }).code === "string") {
    return (e as { code: string }).code;
  }
  return "";
}

function mapFirebaseAuthError(code: string): string {
  switch (code) {
    case "auth/email-already-in-use":
      return "このメールアドレスはすでに登録されています";
    case "auth/invalid-email":
      return "メールアドレスの形式が正しくありません";
    case "auth/weak-password":
      return "パスワードが弱すぎます。別のパスワードをお試しください";
    case "auth/user-not-found":
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "メールアドレスまたはパスワードが違います";
    case "auth/popup-closed-by-user":
      return "ログインがキャンセルされました";
    case "auth/unauthorized-domain":
      return "このドメインではログインできません。Firebase Console → Authentication → 設定 → 承認済みドメインに、ブラウザのアドレスのホスト（例: 192.168.1.8 や localhost）を追加してください。";
    case "auth/operation-not-allowed":
      return "このログイン方法は Firebase Console で有効になっていません。Authentication → Sign-in method で「メール／パスワード」をオンにしてください。";
    case "auth/network-request-failed":
      return "ネットワークエラーです。Wi‑Fi とファイアウォールを確認し、再度お試しください。";
    case "auth/too-many-requests":
      return "試行回数が多すぎます。しばらく待ってから再度お試しください。";
    case "auth/internal-error":
      return "認証サービスで一時的なエラーが発生しました。しばらく待ってから再度お試しください。";
    case "auth/invalid-api-key":
      return "Firebase の API キーが無効です。.env の VITE_FIREBASE_API_KEY を確認してください。";
    case "auth/configuration-not-found":
      return "Firebase Authentication の設定が見つかりません。Console で Authentication を有効化し、Web アプリ設定を確認してください。";
    default:
      return code
        ? `認証に失敗しました（${code}）。Firebase Console の Authentication と承認済みドメインを確認してください。`
        : "認証に失敗しました";
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [user, setUser] = useState<MemberUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const authStateReceived = useRef(false);
  const pushInitUidRef = useRef<string | null>(null);

  const firebaseConfigured = isFirebaseConfigured();

  useEffect(() => {
    if (!firebaseConfigured) {
      setFirebaseUser(null);
      setUser(null);
      setLoading(false);
      setAuthReady(true);
      return;
    }

    authStateReceived.current = false;
    let unsub: (() => void) | undefined;
    let cancelled = false;
    const stallTimer = window.setTimeout(() => {
      if (cancelled || authStateReceived.current) return;
      setError(
        "認証の準備に時間がかかっています。ネットワークと Firebase 設定を確認し、問題が続く場合はアプリを再読み込みしてください。",
      );
      setLoading(false);
      setAuthReady(true);
    }, AUTH_STATE_STALL_MS);

    async function boot() {
      try {
        const auth = getFirebaseAuth();
        try {
          await Promise.race([
            getRedirectResult(auth),
            new Promise<undefined>((resolve) => {
              window.setTimeout(() => resolve(undefined), GET_REDIRECT_RESULT_MS);
            }),
          ]);
        } catch (e: unknown) {
          const code = firebaseErrorCode(e);
          setError(
            code
              ? mapFirebaseAuthError(code)
              : e instanceof Error
                ? e.message
                : "ログインに失敗しました",
          );
        }
        if (cancelled) return;

        unsub = onAuthStateChanged(auth, async (fbUser) => {
          authStateReceived.current = true;
          window.clearTimeout(stallTimer);
          if (!fbUser) {
            pushInitUidRef.current = null;
            setFirebaseUser(null);
            setUser(null);
            setLoading(false);
            setAuthReady(true);
            return;
          }
          setError(null);
          setFirebaseUser(fbUser);
          setLoading(true);
          setAuthReady(true);
          try {
            const health = await api.fetchHealth();
            if (health.firebaseAdmin === false) {
              throw new Error(
                "API の Firebase Admin が未設定です。.env の GOOGLE_APPLICATION_CREDENTIALS または FIREBASE_SERVICE_ACCOUNT_JSON を確認し、npm run dev:api を再起動してください。",
              );
            }
            const u = await loadMemberProfile(fbUser);
            setUser(u);
            if (pushInitUidRef.current !== fbUser.uid) {
              pushInitUidRef.current = fbUser.uid;
              void import("@/lib/returnVisitPush").then(({ initReturnVisitPush }) =>
                initReturnVisitPush(() => fbUser.getIdToken()),
              );
            }
          } catch (e) {
            const msg = e instanceof Error ? e.message : "会員情報の読み込みに失敗しました";
            setError(msg);
            setUser(null);
            await signOut(auth);
            setFirebaseUser(null);
          } finally {
            setLoading(false);
            setAuthReady(true);
          }
        });
      } catch (e) {
        window.clearTimeout(stallTimer);
        const msg = e instanceof Error ? e.message : "Firebase の初期化に失敗しました";
        setError(msg);
        setFirebaseUser(null);
        setUser(null);
        setLoading(false);
        setAuthReady(true);
      }
    }

    void boot();

    return () => {
      cancelled = true;
      window.clearTimeout(stallTimer);
      unsub?.();
    };
  }, [firebaseConfigured]);

  const getIdToken = useCallback(async (forceRefresh = false) => {
    if (!isFirebaseConfigured()) return null;
    const auth = getFirebaseAuth();
    const u = auth.currentUser;
    if (!u) return null;
    return u.getIdToken(forceRefresh);
  }, []);

  const refreshUser = useCallback(async () => {
    if (!isFirebaseConfigured()) return;
    const auth = getFirebaseAuth();
    const u = auth.currentUser;
    if (!u) return;
    setLoading(true);
    setError(null);
    try {
      const prof = await loadMemberProfile(u);
      setUser(prof);
    } catch (e) {
      setError(e instanceof Error ? e.message : "更新に失敗しました");
    } finally {
      setLoading(false);
    }
  }, []);

  const loginWithEmail = useCallback(async (email: string, password: string) => {
    setError(null);
    if (!isFirebaseConfigured()) {
      const msg =
        "Firebase が未設定です。プロジェクト直下の env.example を参考に .env に VITE_FIREBASE_* を設定し、開発サーバーを再起動してください。";
      setError(msg);
      throw new Error(msg);
    }
    try {
      await signInWithEmailAndPassword(getFirebaseAuth(), email.trim(), password);
    } catch (e: unknown) {
      const code = firebaseErrorCode(e);
      const msg = mapFirebaseAuthError(code);
      setError(msg);
      throw e;
    }
  }, []);

  const registerWithEmail = useCallback(
    async (
      name: string,
      email: string,
      password: string,
      consents: api.RegistrationConsents,
      birthMonth: number,
    ) => {
    setError(null);
    if (!isFirebaseConfigured()) {
      const msg =
        "Firebase が未設定です。プロジェクト直下の env.example を参考に .env に VITE_FIREBASE_* を設定し、開発サーバーを再起動してください。";
      setError(msg);
      throw new Error(msg);
    }
    const auth = getFirebaseAuth();
    try {
      const trimmed = name.trim();
      const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
      await api.profileInit(await cred.user.getIdToken(true), {
        name: trimmed || cred.user.email?.split("@")[0] || "ゲスト",
        consents,
        birthMonth,
      });
      if (trimmed) {
        await updateProfile(cred.user, { displayName: trimmed });
      }
    } catch (e: unknown) {
      const code = firebaseErrorCode(e);
      const msg = code.startsWith("auth/")
        ? mapFirebaseAuthError(code)
        : e instanceof Error
          ? e.message
          : "登録に失敗しました";
      setError(msg);
      throw e;
    }
  }, []);

  const loginWithGoogle = useCallback(async () => {
    setError(null);
    if (!isFirebaseConfigured()) {
      const msg =
        "Firebase が未設定です。.env に VITE_FIREBASE_* を設定し、開発サーバーを再起動してください。";
      setError(msg);
      throw new Error(msg);
    }
    try {
      await signInWithRedirect(getFirebaseAuth(), new GoogleAuthProvider());
    } catch (e: unknown) {
      const code = firebaseErrorCode(e);
      setError(mapFirebaseAuthError(code));
      throw e;
    }
  }, []);

  const loginWithApple = useCallback(async () => {
    setError(null);
    if (!isFirebaseConfigured()) {
      const msg =
        "Firebase が未設定です。.env に VITE_FIREBASE_* を設定し、開発サーバーを再起動してください。";
      setError(msg);
      throw new Error(msg);
    }
    try {
      const p = new OAuthProvider("apple.com");
      p.addScope("email");
      p.addScope("name");
      await signInWithRedirect(getFirebaseAuth(), p);
    } catch (e: unknown) {
      const code = firebaseErrorCode(e);
      setError(mapFirebaseAuthError(code));
      throw e;
    }
  }, []);

  const sendPasswordReset = useCallback(async (email: string) => {
    setError(null);
    if (!isFirebaseConfigured()) {
      const msg =
        "Firebase が未設定です。.env に VITE_FIREBASE_* を設定し、開発サーバーを再起動してください。";
      setError(msg);
      throw new Error(msg);
    }
    const trimmed = email.trim();
    if (!trimmed) {
      const msg = "メールアドレスを入力してください";
      setError(msg);
      throw new Error(msg);
    }
    try {
      await sendPasswordResetEmail(getFirebaseAuth(), trimmed);
    } catch (e: unknown) {
      const code = firebaseErrorCode(e);
      setError(mapFirebaseAuthError(code));
      throw e;
    }
  }, []);

  const logout = useCallback(async () => {
    setError(null);
    if (!isFirebaseConfigured()) return;
    pushInitUidRef.current = null;
    try {
      const { teardownReturnVisitPush } = await import("@/lib/returnVisitPush");
      await teardownReturnVisitPush(getIdToken);
    } catch {
      /* noop */
    }
    await signOut(getFirebaseAuth());
  }, [getIdToken]);

  const clearError = useCallback(() => setError(null), []);

  const value = useMemo(
    () => ({
      firebaseConfigured,
      firebaseUser,
      user,
      loading,
      authReady,
      error,
      clearError,
      getIdToken,
      loginWithEmail,
      registerWithEmail,
      loginWithGoogle,
      loginWithApple,
      sendPasswordReset,
      logout,
      refreshUser,
    }),
    [
      firebaseConfigured,
      firebaseUser,
      user,
      loading,
      authReady,
      error,
      clearError,
      getIdToken,
      loginWithEmail,
      registerWithEmail,
      loginWithGoogle,
      loginWithApple,
      sendPasswordReset,
      logout,
      refreshUser,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- hook paired with Provider
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
